import { SegmentedControl, type SegmentedItem } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type * as React from "react";
import { useMemo } from "react";

import {
  type WorkspaceTab,
  selectSetSubTab,
  selectSubTabFor,
  useWorkspaceStore,
} from "@/features/workspace-router/state/workspaceStore";
import { type SubTabDef, WORKSPACE_SUB_TABS } from "../config";

export interface SubTabBarProps {
  tabId: WorkspaceTab;
  /**
   * Optional per-instance override for the chip set. When omitted the
   * bar renders the static :data:`WORKSPACE_SUB_TABS[tabId]` definition.
   * Hubs use this to inject dynamic badge counts (e.g. unread emails,
   * pending PEC, F24 scadenze entro 7 giorni).
   */
  chips?: readonly SubTabDef[];
  /**
   * Optional override for the active chip id. Defaults to the value
   * stored in :func:`useWorkspaceStore.subTabByTab[tabId]`, falling
   * back to the first chip when nothing has been selected yet.
   */
  activeId?: string;
  /**
   * Optional override for the selection handler. Defaults to writing
   * into the store via :func:`selectSetSubTab` so the chip choice
   * survives a reload and Ando's intent classifier can pre-select it.
   */
  onSelect?: (id: string) => void;
  className?: string;
}

/**
 * SubTabBar — secondary chip-bar under the 6 top tabs (v18 mockup
 * ``.surface-chips``). Driven by ``WORKSPACE_SUB_TABS[tabId]`` from the
 * centralized config — never hardcode chips inline. Active state is
 * persisted in ``workspaceStore.subTabByTab[tabId]`` so:
 *
 *   1. Reload preserves the user's filter choice.
 *   2. Ando's chat-intent router writes here via ``setSubTab(_, _,
 *      "ando")`` so the workspace auto-tailors to the query domain
 *      (e.g. *"fatture in scadenza"* → ``finance`` + chip ``sdi``).
 *   3. Hubs read the same store slice via ``useActiveSubTab(tabId)``
 *      and filter their data accordingly.
 */
export const SubTabBar: React.FC<SubTabBarProps> = ({
  tabId,
  chips,
  activeId,
  onSelect,
  className,
}) => {
  const { i18n } = useLingui();
  const storeActive = useWorkspaceStore(useMemo(() => selectSubTabFor(tabId), [tabId]));
  const setSubTab = useWorkspaceStore(selectSetSubTab);

  const effectiveChips: readonly SubTabDef[] =
    chips && chips.length > 0 ? chips : (WORKSPACE_SUB_TABS[tabId] ?? []);
  const fallbackId = effectiveChips[0]?.id;
  const selected = activeId ?? storeActive ?? fallbackId;

  if (effectiveChips.length === 0) return null;

  const handleSelect = (id: string) => {
    if (onSelect) {
      onSelect(id);
      return;
    }
    setSubTab(tabId, id, "manual");
  };

  const options: readonly SegmentedItem<string>[] = effectiveChips.map((chip) => ({
    value: chip.id,
    label: typeof chip.label === "string" ? chip.label : i18n._(chip.label),
    badge: chip.badge,
  }));

  return (
    // `.filter-row` — the padded scroll rail; the segmented control (a proper
    // track+thumb) lives inside it, directly above the list. No border-bottom.
    <div
      className={
        `flex shrink-0 items-center overflow-x-auto px-8 py-3.5${className ? ` ${className}` : ""}`
      }
    >
      <SegmentedControl<string>
        options={options}
        value={selected ?? options[0]?.value ?? ""}
        onChange={handleSelect}
        ariaLabel={i18n._(msg`Filtri`)}
      />
    </div>
  );
};
SubTabBar.displayName = "SubTabBar";

/**
 * Convenience hook for hub bodies: returns the currently-selected
 * sub-tab for ``tabId``, falling back to the first static chip when
 * the store has no entry yet. Memoised so re-renders only fire when
 * the slice actually changes.
 */
export function useActiveSubTab(tabId: WorkspaceTab): string {
  const stored = useWorkspaceStore(useMemo(() => selectSubTabFor(tabId), [tabId]));
  const fallback = WORKSPACE_SUB_TABS[tabId]?.[0]?.id ?? "";
  return stored ?? fallback;
}
